import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Pencil, Search } from 'lucide-react';

const STRATA = ['coal', 'shale', 'clay', 'weathered_ob', 'hard_ob', 'blasted_hard_rock', 'mixed_strata'];
const empty = { name: '', code: '', company_id: '', location: '', location_id: '', city: '', state: '', region: '', strata: [] };

export default function SitesPage() {
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');

  useEffect(() => {
    Promise.all([api.get('/sites'), api.get('/companies'), api.get('/locations')]).then(([s, c, l]) => { setData(s.data); setCompanies(c.data); setLocations(l.data || []); }).finally(() => setLoading(false));
  }, []);
  const fetch = () => api.get('/sites').then(r => setData(r.data));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code || !form.company_id || !form.location_id) { toast.error('Site name, code, company, and location are required'); return; }
    try {
      if (editing) { await api.put(`/sites/${editing}`, form); toast.success('Site updated'); }
      else { await api.post('/sites', form); toast.success('Site created'); }
      setOpen(false); setForm(empty); setEditing(null); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving'); }
  };

  const toggleStatus = async (s) => {
    const ns = s.status === 'active' ? 'inactive' : 'active';
    await api.patch(`/sites/${s.site_id}/status`, { status: ns }); toast.success(`Site ${ns}`); fetch();
  };

  const toggleStrata = (val) => {
    setForm(f => ({ ...f, strata: f.strata.includes(val) ? f.strata.filter(s => s !== val) : [...f.strata, val] }));
  };

  const edit = (s) => { setForm({ name: s.name, code: s.code, company_id: s.company_id, location: s.location || '', location_id: s.location_id || '', city: s.city || '', state: s.state || '', region: s.region || '', strata: s.strata || [] }); setEditing(s.site_id); setOpen(true); };
  const companyName = (id) => companies.find(c => c.company_id === id)?.name || id;
  const locationName = (s) => {
    if (s.location_id) {
      const loc = locations.find(l => l.location_id === s.location_id);
      return loc?.name || s.location || '';
    }
    return s.location || '';
  };
  const handleLocationChange = (locId) => {
    const loc = locations.find(l => l.location_id === locId);
    if (loc) {
      setForm(f => ({ ...f, location_id: locId, location: loc.name, state: loc.state || f.state, region: loc.region || f.region }));
    }
  };
  // Filter locations by selected company
  const companyLocations = form.company_id ? locations.filter(l => l.company_id === form.company_id && l.status !== 'inactive') : locations.filter(l => l.status !== 'inactive');
  const filtered = data.filter(s => (filterCompany === 'all' || s.company_id === filterCompany) && (s.name.toLowerCase().includes(search.toLowerCase()) || s.code.toLowerCase().includes(search.toLowerCase())));

  return (
    <div data-testid="sites-page">
      <div className="page-header">
        <div><h1 className="page-title">Sites</h1><p className="text-sm text-slate-500 mt-1">{data.length} total sites</p></div>
        <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-site-btn"><Plus size={16} className="mr-1.5" />Add Site</Button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative max-w-sm"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Search sites..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" data-testid="search-sites" /></div>
        <Select value={filterCompany} onValueChange={setFilterCompany}><SelectTrigger className="w-48 bg-white" data-testid="filter-company"><SelectValue placeholder="All Companies" /></SelectTrigger><SelectContent><SelectItem value="all">All Companies</SelectItem>{companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Strata</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No sites found</TableCell></TableRow> :
            filtered.map(s => (
              <TableRow key={s.site_id} className="hover:bg-slate-50/50" data-testid={`site-row-${s.site_id}`}>
                <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{s.code}</TableCell>
                <TableCell className="text-sm text-slate-600">{companyName(s.company_id)}</TableCell>
                <TableCell className="text-sm text-slate-600">{locationName(s)}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(s.strata || []).map(st => <span key={st} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{st.replace(/_/g, ' ')}</span>)}</div></TableCell>
                <TableCell><button onClick={() => toggleStatus(s)} className={`status-badge cursor-pointer status-${s.status}`}>{s.status}</button></TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => edit(s)} data-testid={`edit-site-${s.site_id}`}><Pencil size={14} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Site' : 'Add Site'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="grid grid-cols-2 gap-4 mt-2">
            <div className="form-field"><Label>Site Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-50" data-testid="site-name-input" /></div>
            <div className="form-field"><Label>Site Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="bg-slate-50" data-testid="site-code-input" /></div>
            <div className="form-field col-span-2">
              <Label>Company *</Label>
              <Select value={form.company_id} onValueChange={v => setForm({...form, company_id: v})}>
                <SelectTrigger className="bg-slate-50" data-testid="site-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.filter(c => c.status === 'active').map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="form-field">
              <Label>Location / Mining Area *</Label>
              <Select value={form.location_id} onValueChange={handleLocationChange}>
                <SelectTrigger className="bg-slate-50" data-testid="site-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {companyLocations.length === 0 ? (
                    <SelectItem value="_none" disabled>No locations available — add one first</SelectItem>
                  ) : (
                    companyLocations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name} {l.state ? `(${l.state})` : ''}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="form-field"><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>State</Label><Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field"><Label>Region</Label><Input value={form.region} onChange={e => setForm({...form, region: e.target.value})} className="bg-slate-50" /></div>
            <div className="form-field col-span-2">
              <Label>Strata Configuration</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5">
                {STRATA.map(st => (
                  <label key={st} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <Checkbox checked={form.strata.includes(st)} onCheckedChange={() => toggleStrata(st)} data-testid={`strata-${st}`} />
                    <span className="capitalize">{st.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-site-btn">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
